import { useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Download, Trash2, ShieldCheck } from "lucide-react";

export const AccountSection = () => {
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const acceptance = user?.legal_acceptance || null;
  const acceptedAt = acceptance?.accepted_at || user?.gdpr_consent_at || null;
  const fmt = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "first2job-dados.json";
      a.click();
      toast.success("Dados exportados (JSON)");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/me/delete-account", { password });
      toast.success("Conta eliminada. Até breve!");
      setOpen(false);
      await logout();
      window.location.href = "/";
    } catch (e) { toast.error(formatErr(e)); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="account-section" className="border border-black/5 rounded-xl p-5 bg-slate-50">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-1 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Privacidade e dados (RGPD)
      </p>
      <p className="text-xs text-slate-500 mb-4">Podes exportar todos os teus dados ou eliminar a conta a qualquer momento.</p>
      {acceptedAt && (
        <div data-testid="account-consent-info" className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2.5 mb-4">
          <span className="font-semibold text-slate-900">Consentimento registado</span> em {fmt(acceptedAt)}
          {acceptance?.terms_version && (
            <> — Termos v{acceptance.terms_version}
              {acceptance?.privacy_version ? ` · Privacidade v${acceptance.privacy_version}` : ""}</>
          )}
          <span className="block mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
            <a href="/legal/termos-e-condicoes" target="_blank" rel="noreferrer" className="underline decoration-amber-400 decoration-2 underline-offset-2 hover:text-slate-900">Ver Termos</a>
            <a href="/legal/politica-de-privacidade" target="_blank" rel="noreferrer" className="underline decoration-amber-400 decoration-2 underline-offset-2 hover:text-slate-900">Ver Política de Privacidade</a>
            <button type="button" onClick={() => window.dispatchEvent(new Event("f2j:open-cookie-prefs"))}
              className="underline decoration-amber-400 decoration-2 underline-offset-2 hover:text-slate-900">
              Preferências de cookies
            </button>
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button data-testid="export-data-btn" size="sm" variant="outline" className="rounded-full" onClick={exportData}>
          <Download className="w-3.5 h-3.5 mr-1.5" /> Exportar os meus dados
        </Button>
        <Button data-testid="delete-account-btn" size="sm" variant="ghost" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setOpen(true)}>
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar conta
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-account-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading tracking-tighter">Eliminar conta</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Esta ação é irreversível: os teus dados pessoais são removidos. Confirma com a tua password.</p>
          <Input data-testid="delete-account-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
          <Button data-testid="delete-account-confirm" variant="destructive" className="rounded-full" disabled={!password || deleting} onClick={deleteAccount}>
            {deleting ? "A eliminar..." : "Eliminar definitivamente"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
